'use client';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Factory, Plus, Search, X, Edit2, Trash2,
  Loader2, Calendar, Scale, ImageIcon, Hash,
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { finishedProductsService, type FinishedProduct, type MaterialUnit } from '@/lib/supabase/services';
import ImageUpload from '@/components/ui/ImageUpload';
import AppImage from '@/components/ui/AppImage';

const UNITS: MaterialUnit[] = ['كجم', 'طن', 'لتر', 'متر', 'قطعة', 'صندوق', 'كيس', 'برميل'];

function formatDate(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

const EMPTY_FORM = {
  name: '', quantity: '', unit: 'طن' as MaterialUnit,
  imageUrl: '', productionDate: new Date().toISOString().split('T')[0],
  batchNumber: '', notes: '',
};

export default function FinishedProductsContent() {
  const [products, setProducts] = useState<FinishedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<FinishedProduct | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [viewImage, setViewImage] = useState<{ url: string; name: string } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await finishedProductsService.getAll();
      setProducts(data);
    } catch (err: any) {
      toast.error('فشل تحميل البيانات: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = products.filter((p) =>
    !searchQuery || p.name.includes(searchQuery) || p.batchNumber.includes(searchQuery)
  );

  const totalTons = products.filter((p) => p.unit === 'طن').reduce((sum, p) => sum + p.quantity, 0);

  const openAdd = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (item: FinishedProduct) => {
    setEditingItem(item);
    setForm({
      name: item.name, quantity: String(item.quantity), unit: item.unit,
      imageUrl: item.imageUrl, productionDate: item.productionDate,
      batchNumber: item.batchNumber, notes: item.notes,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.quantity) { toast.error('يرجى ملء الحقول المطلوبة'); return; }
    const qty = parseFloat(form.quantity);
    if (isNaN(qty) || qty < 0) { toast.error('الكمية يجب أن تكون رقماً صحيحاً'); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(), quantity: qty, unit: form.unit,
        imageUrl: form.imageUrl, productionDate: form.productionDate,
        batchNumber: form.batchNumber, notes: form.notes,
      };
      if (editingItem) {
        await finishedProductsService.update(editingItem.id, payload);
        toast.success('تم تحديث المنتج بنجاح');
      } else {
        await finishedProductsService.create(payload);
        toast.success('تم إضافة المنتج بنجاح');
      }
      setShowForm(false);
      loadData();
    } catch (err: any) {
      toast.error('فشل الحفظ: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`هل أنت متأكد من حذف "${name}"؟`)) return;
    try {
      await finishedProductsService.delete(id);
      toast.success('تم حذف المنتج');
      loadData();
    } catch (err: any) {
      toast.error('فشل الحذف: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 xl:p-6" dir="rtl">
      <Toaster position="bottom-right" richColors />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-success/15 border border-success/30 flex items-center justify-center">
            <Factory size={20} className="text-success" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">إدارة المنتج التام</h1>
            <p className="text-sm text-muted-foreground">متابعة مخزون المنتجات النهائية للمصنع</p>
          </div>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-5 py-2.5 bg-accent text-accent-foreground rounded-xl font-bold text-sm hover:opacity-90 transition-all active:scale-95"
        >
          <Plus size={16} />
          إضافة منتج
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">إجمالي المنتجات</p>
          <p className="text-3xl font-bold text-foreground tabular-nums">{products.length}</p>
        </div>
        <div className="bg-card border border-success/30 rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">إجمالي الأطنان</p>
          <p className="text-3xl font-bold text-success tabular-nums">{totalTons.toFixed(1)}</p>
        </div>
        <div className="bg-card border border-accent/30 rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">أُنتج اليوم</p>
          <p className="text-3xl font-bold text-accent tabular-nums">
            {products.filter((p) => p.productionDate === new Date().toISOString().split('T')[0]).length}
          </p>
        </div>
        <div className="bg-card border border-warning/30 rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">بصور مرفوعة</p>
          <p className="text-3xl font-bold text-warning tabular-nums">{products.filter((p) => p.imageUrl).length}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="بحث باسم المنتج أو رقم الدفعة..."
          className="w-full bg-card border border-border rounded-xl pr-9 pl-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Grid view */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="text-accent animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <Factory size={40} className="mx-auto mb-3 opacity-30" />
          <p>لا توجد منتجات مسجلة</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((item) => (
            <div key={item.id} className="bg-card border border-border rounded-2xl overflow-hidden group">
              {/* Product image */}
              <div
                className="h-44 bg-muted flex items-center justify-center relative overflow-hidden cursor-pointer"
                onClick={() => item.imageUrl && setViewImage({ url: item.imageUrl, name: item.name })}
              >
                {item.imageUrl ? (
                  <AppImage src={item.imageUrl} alt={`صورة منتج ${item.name}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <ImageIcon size={32} className="opacity-40" />
                    <p className="text-xs">لا توجد صورة</p>
                  </div>
                )}
                {item.batchNumber && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 text-white text-xs px-2 py-1 rounded-lg">
                    <Hash size={10} />
                    {item.batchNumber}
                  </div>
                )}
              </div>

              {/* Product info */}
              <div className="p-4 space-y-3">
                <h3 className="font-bold text-foreground text-base">{item.name}</h3>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1.5 text-sm font-bold text-success">
                    <Scale size={13} />
                    {item.quantity.toLocaleString('ar-EG')} {item.unit}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Calendar size={12} />
                    {formatDate(item.productionDate)}
                  </span>
                </div>
                {item.notes && <p className="text-xs text-muted-foreground line-clamp-2">{item.notes}</p>}
                <div className="flex gap-2 pt-1 border-t border-border">
                  <button
                    onClick={() => openEdit(item)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-muted text-muted-foreground hover:text-foreground text-xs font-medium transition-all"
                  >
                    <Edit2 size={12} />
                    تعديل
                  </button>
                  <button
                    onClick={() => handleDelete(item.id, item.name)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-danger/10 text-danger hover:bg-danger/20 text-xs font-medium transition-all"
                  >
                    <Trash2 size={12} />
                    حذف
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
            <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
              <h2 className="font-bold text-foreground">{editingItem ? 'تعديل المنتج' : 'إضافة منتج جديد'}</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">اسم المنتج *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="مثال: سماد NPK 20-20-20..."
                  className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">الكمية *</label>
                  <input
                    type="number"
                    value={form.quantity}
                    onChange={(e) => setForm((p) => ({ ...p, quantity: e.target.value }))}
                    placeholder="0.000"
                    min="0"
                    step="0.001"
                    className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">الوحدة *</label>
                  <select
                    value={form.unit}
                    onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value as MaterialUnit }))}
                    className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">تاريخ الإنتاج *</label>
                  <input
                    type="date"
                    value={form.productionDate}
                    onChange={(e) => setForm((p) => ({ ...p, productionDate: e.target.value }))}
                    className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">رقم الدفعة</label>
                  <input
                    type="text"
                    value={form.batchNumber}
                    onChange={(e) => setForm((p) => ({ ...p, batchNumber: e.target.value }))}
                    placeholder="B-2026-001"
                    className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
              <ImageUpload
                bucket="products"
                currentUrl={form.imageUrl}
                onUpload={(url) => setForm((p) => ({ ...p, imageUrl: url }))}
                label="صورة المنتج"
                folder="finished-products"
              />
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">ملاحظات</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  rows={2}
                  placeholder="ملاحظات إضافية..."
                  className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-accent text-accent-foreground rounded-xl font-bold text-sm hover:opacity-90 transition-all disabled:opacity-50"
                >
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  {editingItem ? 'حفظ التعديلات' : 'إضافة المنتج'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 bg-muted text-muted-foreground rounded-xl font-medium text-sm hover:text-foreground transition-all">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image viewer */}
      {viewImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setViewImage(null)}>
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setViewImage(null)} className="absolute -top-10 left-0 text-white/70 hover:text-white">
              <X size={24} />
            </button>
            <p className="text-white text-center mb-3 font-semibold">{viewImage.name}</p>
            <AppImage src={viewImage.url} alt={viewImage.name} className="w-full rounded-2xl" />
          </div>
        </div>
      )}
    </div>
  );
}
